import React from "react";
import { ButtonColour, ButtonSize } from "@trussle/external-components";
import classnames from "classnames";

import { startsWithASingleSlash } from "../../helpers/startsWithASingleSlash";
import { ICTA, ICTAWithTracking } from "../../types/ICTA";
import {
  InternalCTALink,
  IProps as IInternalCTAProps,
} from "./internalCTALink";
import { Partner, usePartnerContext } from "../Partner";
import { uSwitchStylesPrimary, uSwitchStylesSecondary } from "./partners/uSwitch";
import { trackEvent } from "../../helpers/analytics";
import { usePageTitle } from "../PageTitle";

import "./styles.scss";

interface IProps {
  alignment?: "center" | "left";
  size?: ButtonSize;
  centerMobileTablet?: boolean;
  containerClassName?: string;
  className?: string;
  ctas: ICTAWithTracking[];
  primaryCtaColour?: ButtonColour;
  secondaryCtaColour?: ButtonColour;
}

const PRIMARY_STYLES_MAP = {
  [Partner.U_SWITCH]: uSwitchStylesPrimary,
  [Partner.NONE]: {},
  [Partner.EMMA]: {},
  [Partner.FORBES]: {},
  [Partner.BRITISH_HOME_SELLERS]: {},
};

const SECONDARY_STYLES_MAP = {
  [Partner.U_SWITCH]: uSwitchStylesSecondary,
  [Partner.NONE]: {},
  [Partner.EMMA]: {},
  [Partner.FORBES]: {},
  [Partner.BRITISH_HOME_SELLERS]: {},
};

const CallToActions: React.FC<IProps> = (props) => {
  const {
    alignment = "center",
    size = ButtonSize.Large,
    centerMobileTablet,
    ctas,
    containerClassName,
    className,
    primaryCtaColour = ButtonColour.Primary,
    secondaryCtaColour = ButtonColour.Secondary,
    ...rest
  } = props;
  const page = usePageTitle();

  const ctasWithTracking = ctas.map((cta) => {
    const handleClick = (event: any) => {
      trackEvent("Call to Action Clicked", {
        module: cta.trackingModule,
        value: cta.title,
        page,
      });
      return cta.handleClick && cta.handleClick(event);
    };
    return {
      ...cta,
      trackingModule: undefined,
      handleClick,
    };
  });

  const containerClasses = classnames("cta", containerClassName, {
    "cta-alignCenter": alignment === "center",
    "cta-alignLeft": alignment === "left",
    "cta-centerMobileTablet": centerMobileTablet,
  });

  const partner = usePartnerContext();
  const buttonClasses = classnames(className, "cta-wrapper", {
    "center-single-cta": ctasWithTracking.length === 1 && alignment === "center",
    "single-cta": ctasWithTracking.length === 1,
    "single-cta-centerMobileTablet": ctasWithTracking.length === 1 && centerMobileTablet,
  }, `partner-${partner}`);
  const secondaryButtonClasses = classnames(buttonClasses, "secondary-cta");
  const primaryStyles = PRIMARY_STYLES_MAP[partner];
  const secondaryStyles = SECONDARY_STYLES_MAP[partner];

  const isInternal = ctas.map((cta) => startsWithASingleSlash.test(cta.url));

  const internalCtaLinkProps1: IInternalCTAProps = {
    ...rest,
    ...ctasWithTracking[0],
    buttonClasses,
    ctaColour: primaryCtaColour,
    isInternal: isInternal[0],
    size,
    style: primaryStyles,
  };

  const internalCtaLinkProps2: IInternalCTAProps = {
    ...internalCtaLinkProps1,
    ...ctasWithTracking[1],
    ctaColour: secondaryCtaColour,
    isInternal: isInternal[1],
    buttonClasses: secondaryButtonClasses,
    style: secondaryStyles,
  };

  return (
    <div role="region" aria-label="Call to action" className={containerClasses}>
      <InternalCTALink {...internalCtaLinkProps1} />
      {ctasWithTracking[1] && <InternalCTALink {...internalCtaLinkProps2} />}
    </div>
  );
};

const addTracking = (trackingModule: string, ...ctas: ICTA[]) => ctas.map((cta) => ({
  ...cta,
  trackingModule,
}));

export { CallToActions, IProps, addTracking };
